// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { ClassContext, Message, Session, ThreadTask } from '@/types';
import { selectCurrentThreadId } from './chatStore';
import type { ThreadRuntimeView } from './chatStore.threadView';

export const DEFAULT_DRAFT_ACTOR_MODE: SessionStoreSnapshot['draftActorMode'] = 'student';

/**
 * CI-2 guard: a rehydrated session may only rebuild its empty thread view
 * when the persisted session belongs to the currently authenticated user.
 *
 * - no authenticated user -> false (never rebuild for an unknown identity)
 * - session owner differs from the auth user -> false (cross-user leak)
 * - session owner matches -> true (characterization: same-user rebuild path)
 */
export function shouldRebuildThreadViewOnRehydrate(
  session: Session | null,
  authUserId: string | null
): boolean {
  if (!session) {
    return false;
  }
  if (!authUserId) {
    return false;
  }
  return session.user_id === authUserId;
}

/**
 * CI-H1: sanitize a rehydrated persisted slice against the authenticated
 * user. Returns the patch to apply onto the rehydrated state, or null when
 * the slice may be kept as-is.
 *
 * - no authenticated user -> keep (CI-2 still blocks the view rebuild; the
 *   post-login reconcile is the cleanup authority there)
 * - currentSession owned by another user -> drop the whole persisted slice
 *   (draft reset: sessions / classContext / tasks / thread ids all cleared)
 * - no currentSession but foreign sessions present -> filter them out,
 *   drop thread tasks for foreign threads and the unattributable class
 *   context; same-user sessions survive
 */
export function sanitizeRehydratedStateForUser(
  state: Pick<
    SessionStoreSnapshot,
    'currentSession' | 'sessions' | 'threadTasksByThread'
  >,
  authUserId: string | null
): Partial<SessionStoreSnapshot> | null {
  if (!authUserId) {
    return null;
  }

  if (state.currentSession && state.currentSession.user_id !== authUserId) {
    return {
      ...buildDraftSessionState(),
      currentThreadView: null,
      classContext: null,
      sessions: [],
      threadTasksByThread: {},
      sessionsLoadedAt: 0,
    };
  }

  const foreignSessionsPresent = state.sessions.some(
    (session) => session.user_id !== authUserId
  );
  if (!state.currentSession && foreignSessionsPresent) {
    const allowedThreadIds = new Set(
      state.sessions
        .filter((session) => session.user_id === authUserId)
        .map((session) => session.thread_id)
    );
    return {
      currentThreadView: null,
      classContext: null,
      sessions: state.sessions.filter((session) => session.user_id === authUserId),
      threadTasksByThread: Object.fromEntries(
        Object.entries(state.threadTasksByThread).filter(([threadId]) =>
          allowedThreadIds.has(threadId)
        )
      ),
      sessionsLoadedAt: 0,
    };
  }

  return null;
}

export interface SessionStoreSnapshot {
  currentSession: Session | null;
  currentThreadId: string | null;
  currentThreadView?: ThreadRuntimeView | null;
  draftActorMode: 'teacher' | 'student';
  classContext?: ClassContext | null;
  messages: Message[];
  historicalMessageIds: string[];
  currentThreadTasks: ThreadTask[];
  messagesBySession: Record<string, Message[]>;
  threadTasksByThread: Record<string, ThreadTask[]>;
  missingHistoryBySession: Record<string, true>;
  sessions: Session[];
  sessionsLoadedAt: number;
  chatError: string | null;
  backgroundError: string | null;
  checkpoints: unknown[];
  lastReplayCheckpointId: string | null;
}

type ThreadHistoryMissingPatch = Partial<
  Pick<
    SessionStoreSnapshot,
    | 'backgroundError'
    | 'missingHistoryBySession'
    | 'messagesBySession'
    | 'messages'
    | 'historicalMessageIds'
  >
>;

function shouldRetryFreshSessionHistoryMiss(
  state: SessionStoreSnapshot,
  sessionId: string,
  status: number | null
): boolean {
  if (status !== 404) {
    return false;
  }

  const currentSession = state.currentSession;
  if (!currentSession || currentSession.session_id !== sessionId) {
    return false;
  }

  const sessionScopedMessages =
    state.messagesBySession[sessionId] ??
    (currentSession.session_id === sessionId ? state.messages : []);
  const visibleMessageCount = sessionScopedMessages.length;
  const authoritativeMessageCount = currentSession.message_count ?? 0;

  if (visibleMessageCount === 0) {
    return authoritativeMessageCount === 0;
  }

  const hasOnlyUserTurns = sessionScopedMessages.every(
    (message) => message.role === 'user'
  );

  return hasOnlyUserTurns && authoritativeMessageCount <= visibleMessageCount;
}

function applyActorModeToSession(
  session: Session | null,
  actorMode?: Session['actor_mode']
): Session | null {
  if (!session || !actorMode || session.actor_mode === actorMode) {
    return session;
  }
  return {
    ...session,
    actor_mode: actorMode,
  };
}

function applyActorModeToSessions(
  sessions: Session[],
  sessionId: string,
  actorMode?: Session['actor_mode']
): Session[] {
  if (!actorMode) {
    return sessions;
  }
  return sessions.map((session) =>
    session.session_id === sessionId && session.actor_mode !== actorMode
      ? { ...session, actor_mode: actorMode }
      : session
  );
}

export function computeSessionSelectionState(
  state: SessionStoreSnapshot,
  session: Session | null,
  options: {
    cachedMessages?: Message[];
  }
) {
  const cachedMessages = options.cachedMessages;

  return {
    currentSession: session,
    messages: cachedMessages ? [...cachedMessages] : [],
    historicalMessageIds: cachedMessages?.map((message) => message.id) ?? [],
    currentThreadTasks: session?.thread_id
      ? (state.threadTasksByThread[session.thread_id] ?? [])
      : [],
    backgroundError: null,
    checkpoints: [],
    lastReplayCheckpointId: null,
  };
}

export function applyThreadHistoryLoaded(
  state: SessionStoreSnapshot,
  params: {
    sessionId: string;
    threadId: string;
    messages: Message[];
    resolvedActorMode?: Session['actor_mode'];
  }
) {
  // CD-3: derive the active thread id through the canonical selector rather
  // than the raw persisted field. computeSessionSelectionState stopped
  // maintaining `currentThreadId` (round 3), so the raw field can be stale
  // relative to currentThreadView / currentSession.
  const isActiveThread = selectCurrentThreadId(state) === params.threadId;
  const isCurrentSession = state.currentSession?.session_id === params.sessionId;

  return {
    missingHistoryBySession: Object.fromEntries(
      Object.entries(state.missingHistoryBySession).filter(([key]) => key !== params.sessionId)
    ) as Record<string, true>,
    messagesBySession: {
      ...state.messagesBySession,
      [params.sessionId]: params.messages,
    },
    sessions: applyActorModeToSessions(state.sessions, params.sessionId, params.resolvedActorMode),
    currentSession: isCurrentSession
      ? applyActorModeToSession(state.currentSession, params.resolvedActorMode)
      : state.currentSession,
    messages: isCurrentSession || isActiveThread ? params.messages : state.messages,
    historicalMessageIds:
      isCurrentSession || isActiveThread
        ? params.messages.map((message) => message.id)
        : state.historicalMessageIds,
  };
}

export function applyThreadHistoryMissing(
  state: SessionStoreSnapshot,
  params: {
    sessionId: string;
    status: number | null;
    errorMessage: string;
  }
): ThreadHistoryMissingPatch {
  const isCurrentSession = state.currentSession?.session_id === params.sessionId;

  if (params.status !== 404) {
    return { backgroundError: params.errorMessage };
  }

  if (shouldRetryFreshSessionHistoryMiss(state, params.sessionId, params.status)) {
    return { backgroundError: null };
  }

  return {
    backgroundError: params.errorMessage,
    missingHistoryBySession: {
      ...state.missingHistoryBySession,
      [params.sessionId]: true,
    } as Record<string, true>,
    messagesBySession: {
      ...state.messagesBySession,
      [params.sessionId]: [],
    },
    messages: isCurrentSession ? [] : state.messages,
    historicalMessageIds: isCurrentSession ? [] : state.historicalMessageIds,
  };
}

export function applyRuntimeSessionRefresh(
  state: SessionStoreSnapshot,
  params: {
    sessionId: string;
    nextMessages: Message[];
  }
) {
  const updatedAt =
    params.nextMessages[params.nextMessages.length - 1]?.timestamp || new Date().toISOString();
  const nextCurrentSession =
    state.currentSession?.session_id === params.sessionId
      ? {
          ...state.currentSession,
          updated_at: updatedAt,
          message_count: params.nextMessages.length,
        }
      : state.currentSession;
  const refreshedSession = state.sessions.find(
    (session) => session.session_id === params.sessionId
  );
  const nextSessions = refreshedSession
    ? [
        {
          ...refreshedSession,
          updated_at: updatedAt,
          message_count: params.nextMessages.length,
        },
        ...state.sessions.filter((session) => session.session_id !== params.sessionId),
      ]
    : state.sessions;

  return {
    currentSession: nextCurrentSession,
    messages:
      state.currentSession?.session_id === params.sessionId ? params.nextMessages : state.messages,
    historicalMessageIds:
      state.currentSession?.session_id === params.sessionId
        ? state.historicalMessageIds.filter((id) =>
            params.nextMessages.some((message) => message.id === id)
          )
        : state.historicalMessageIds,
    messagesBySession: {
      ...state.messagesBySession,
      [params.sessionId]: params.nextMessages,
    },
    sessions: nextSessions,
  };
}

export function applyAuthReconciled(
  state: SessionStoreSnapshot,
  userId: string | null
): Partial<SessionStoreSnapshot> {
  if (!userId) {
    return {
      currentSession: null,
      currentThreadId: null,
      currentThreadView: null,
      messages: [],
      historicalMessageIds: [],
      currentThreadTasks: [],
      messagesBySession: {},
      threadTasksByThread: {},
      missingHistoryBySession: {},
      sessions: [],
      sessionsLoadedAt: 0,
      chatError: null,
      backgroundError: null,
      checkpoints: [],
      lastReplayCheckpointId: null,
      classContext: null,
      draftActorMode: DEFAULT_DRAFT_ACTOR_MODE,
    };
  }

  const keepCurrentSession = state.currentSession?.user_id === userId ? state.currentSession : null;
  const filteredSessions = state.sessions.filter((session) => session.user_id === userId);
  const prunedSessionScope =
    filteredSessions.length !== state.sessions.length ||
    Boolean(state.currentSession && !keepCurrentSession);
  const allowedSessionIds = new Set(filteredSessions.map((session) => session.session_id));
  if (keepCurrentSession) {
    allowedSessionIds.add(keepCurrentSession.session_id);
  }

  const nextMessagesBySession = Object.fromEntries(
    Object.entries(state.messagesBySession).filter(([sessionId]) =>
      allowedSessionIds.has(sessionId)
    )
  );
  const nextMissingHistoryBySession = Object.fromEntries(
    Object.entries(state.missingHistoryBySession).filter(([sessionId]) =>
      allowedSessionIds.has(sessionId)
    )
  ) as Record<string, true>;
  const allowedThreadIds = new Set(filteredSessions.map((session) => session.thread_id));
  if (keepCurrentSession) {
    allowedThreadIds.add(keepCurrentSession.thread_id);
  }
  const nextThreadTasksByThread = Object.fromEntries(
    Object.entries(state.threadTasksByThread).filter(([threadId]) => allowedThreadIds.has(threadId))
  );

  if (!keepCurrentSession) {
    return {
      currentSession: null,
      currentThreadId: null,
      messages: [],
      historicalMessageIds: [],
      currentThreadTasks: [],
      messagesBySession: nextMessagesBySession,
      threadTasksByThread: nextThreadTasksByThread,
      missingHistoryBySession: nextMissingHistoryBySession,
      sessions: filteredSessions,
      sessionsLoadedAt: 0,
      chatError: null,
      backgroundError: null,
      checkpoints: [],
      lastReplayCheckpointId: null,
      // Identity transition (foreign sessions/current session were pruned):
      // drop the previous user's class/role draft and thread view so they
      // cannot leak into the next identity. On a same-user reconcile with no
      // foreign data this stays untouched.
      currentThreadView: null,
      ...(prunedSessionScope
        ? { classContext: null, draftActorMode: DEFAULT_DRAFT_ACTOR_MODE }
        : {}),
    };
  }

  return {
    currentSession: keepCurrentSession,
    currentThreadId: keepCurrentSession.thread_id,
    currentThreadTasks: nextThreadTasksByThread[keepCurrentSession.thread_id] ?? [],
    sessions: filteredSessions,
    messagesBySession: nextMessagesBySession,
    threadTasksByThread: nextThreadTasksByThread,
    missingHistoryBySession: nextMissingHistoryBySession,
    ...(prunedSessionScope
      ? {
          sessionsLoadedAt: 0,
          chatError: null,
        }
      : {}),
  };
}

export function buildDraftSessionState(mode?: SessionStoreSnapshot['draftActorMode']) {
  return {
    currentSession: null,
    currentThreadId: null,
    draftActorMode: mode ?? DEFAULT_DRAFT_ACTOR_MODE,
    currentThreadTasks: [],
    messages: [],
    historicalMessageIds: [],
    chatError: null,
    backgroundError: null,
  };
}

export function applySessionCreated(state: SessionStoreSnapshot, nextSession: Session) {
  return {
    sessions: [nextSession, ...state.sessions],
    currentSession: nextSession,
    currentThreadId: nextSession.thread_id,
    currentThreadTasks: [],
    messages: [],
    historicalMessageIds: [],
    chatError: null,
    backgroundError: null,
    messagesBySession: {
      ...state.messagesBySession,
      [nextSession.session_id]: [],
    },
    sessionsLoadedAt: Date.now(),
  };
}

export function applySessionDeleted(state: SessionStoreSnapshot, sessionId: string) {
  const isCurrent = state.currentSession?.session_id === sessionId;
  const removedSession = state.sessions.find((session) => session.session_id === sessionId);
  const nextMessagesBySession = { ...state.messagesBySession };
  const nextMissingHistoryBySession = { ...state.missingHistoryBySession };
  const nextThreadTasksByThread = { ...state.threadTasksByThread };

  delete nextMessagesBySession[sessionId];
  delete nextMissingHistoryBySession[sessionId];
  if (removedSession) {
    delete nextThreadTasksByThread[removedSession.thread_id];
  }

  return {
    sessions: state.sessions.filter((session) => session.session_id !== sessionId),
    currentSession: isCurrent ? null : state.currentSession,
    currentThreadId: isCurrent ? null : state.currentThreadId,
    currentThreadTasks: isCurrent ? [] : state.currentThreadTasks,
    messages: isCurrent ? [] : state.messages,
    historicalMessageIds: isCurrent ? [] : state.historicalMessageIds,
    messagesBySession: nextMessagesBySession,
    threadTasksByThread: nextThreadTasksByThread,
    missingHistoryBySession: nextMissingHistoryBySession,
    sessionsLoadedAt: Date.now(),
  };
}

export function applySessionRenamed(state: SessionStoreSnapshot, sessionId: string, title: string) {
  return {
    sessions: state.sessions.map((session) =>
      session.session_id === sessionId ? { ...session, title } : session
    ),
    currentSession:
      state.currentSession?.session_id === sessionId
        ? { ...state.currentSession, title }
        : state.currentSession,
  };
}

export function applySessionArchived(state: SessionStoreSnapshot, sessionId: string) {
  const isCurrent = state.currentSession?.session_id === sessionId;
  return {
    sessions: state.sessions.map((session) =>
      session.session_id === sessionId ? { ...session, is_archived: true } : session
    ),
    currentSession: isCurrent ? null : state.currentSession,
    currentThreadId: isCurrent ? null : state.currentThreadId,
    messages: isCurrent ? [] : state.messages,
    historicalMessageIds: isCurrent ? [] : state.historicalMessageIds,
  };
}

export function applySessionUnarchived(state: SessionStoreSnapshot, sessionId: string) {
  return {
    sessions: state.sessions.map((session) =>
      session.session_id === sessionId ? { ...session, is_archived: false } : session
    ),
  };
}
